import React from "react";
import PropTypes from "prop-types";
import {
  FormattedMessage,
  defineMessages,
  useIntl,
  injectIntl
} from "react-intl";
import styled from "styled-components";

import { Loading, Icon, BREAKPOINTS } from "@onelogin/react-components";
import ResponsiveTable from "../common/Table/ResponsiveTable";
import Moment from "../MomentWrapper";
import {
  // StarredColumn,
  TitleColumn,
  LastUpdatedColumn,
  StatusColumn,
  OwnerColumn
} from "./SecureNotesColumn";

const ICON_IMAGE_SIZE = 24;
const ICON_MARGIN = 16;
const ACCESS_PRIVATE = 0;

const messages = defineMessages({
  deleteNote: {
    id: "deleteNote",
    defaultMessage: "Delete note"
  }
});

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 ${ICON_IMAGE_SIZE + ICON_MARGIN}px;
  width: 44px;
  height: 44px;

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    justify-content: center;
    width: 48px;
    height: 48px;
  }
`;

const LoadingWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const TrashcanIcon = styled(Icon).attrs({
  icon: "Trashcan-24",
  size: "24"
})`
  cursor: pointer;
`;

const SecureNotesTable = props => {
  const intl = useIntl();
  let tableStructureColumns = [
    // {
    //   minWidth: 20,
    //   weight: 7,
    //   priority: 3,
    //   content: "",
    //   key: "starred"
    // },
    {
      minWidth: 100,
      weight: 30,
      priority: 5,
      content: (
        <FormattedMessage defaultMessage="Secure Notes" id="secureNotes" />
      ),
      key: "title"
    }
  ];

  if (!props.showSecureNotePanel) {
    tableStructureColumns = tableStructureColumns.concat([
      {
        minWidth: 150,
        weight: 25,
        priority: 1,
        content: (
          <FormattedMessage defaultMessage="Last Updated" id="lastUpdated" />
        ),
        key: "lastUpdated"
      },
      {
        minWidth: 100,
        weight: 18,
        priority: 3,
        content: <FormattedMessage defaultMessage="Status" id="status" />,
        key: "status"
      },
      {
        minWidth: 100,
        weight: 20,
        priority: 1,
        content: <FormattedMessage defaultMessage="Owner" id="owner" />,
        key: "owner"
      },
      {
        minWidth: 48,
        width: "48px",
        priority: 4, //highest priority to be always displayed
        content: "",
        key: "expander"
      }
    ]);
  }

  const tableStructure = {
    columns: tableStructureColumns
  };

  tableStructure.data = [];

  const {
    userId,
    notes,
    currentNote,
    locale,
    loading,
    showSecureNote,
    onClickRemove,
    showSecureNotePanel
  } = props;

  if (!notes && loading) {
    return (
      <LoadingWrapper>
        <Loading />
      </LoadingWrapper>
    );
  }

  if (notes) {
    for (let note of notes) {
      const {
        id,
        title,
        owner,
        user_id: noteUserId,
        updated_at: lastUpdated,
        access
      } = note; //isStarred
      const isPrivate = access === ACCESS_PRIVATE ? true : false;
      const isOwner = noteUserId === userId ? true : false;

      // const starredCol = (
      //   <StarredColumn>
      //     <IconWrapper>
      //       <Icon name={isStarred ? "Star-Yellow-16" : "Star-Outline-16"} />
      //     </IconWrapper>
      //   </StarredColumn>
      // );

      const titleCol = (
        <TitleColumn>
          <span title={title}>{title}</span>
        </TitleColumn>
      );

      const lastUpdatedCol = (
        <LastUpdatedColumn>
          <span title={lastUpdated}>
            <Moment locale={locale} fromNow>
              {lastUpdated}
            </Moment>
          </span>
        </LastUpdatedColumn>
      );

      const statusCol = (
        <StatusColumn>
          {isPrivate ? (
            <FormattedMessage defaultMessage="Private" id="private" />
          ) : (
            <FormattedMessage defaultMessage="Shared" id="shared" />
          )}
        </StatusColumn>
      );

      const ownerCol = <OwnerColumn>{owner}</OwnerColumn>;

      let cells = [
        {
          key: "id",
          content: id
        },
        // {
        //   key: "starred",
        //   content: starredCol
        // },
        {
          key: "title",
          content: titleCol
        }
      ];

      if (!showSecureNotePanel) {
        cells = cells.concat([
          {
            key: "lastUpdated",
            content: lastUpdatedCol
          },
          {
            key: "status",
            content: statusCol
          },
          {
            key: "owner",
            content: ownerCol
          },

          {
            key: "expander",
            content: isOwner ? (
              <IconWrapper
                onClick={event => {
                  onClickRemove(event, id);
                }}
              >
                <TrashcanIcon data-testid={`noteslist-delete-${id}-button`} alt={intl.formatMessage(messages.deleteNote)} />
              </IconWrapper>
            ) : (
              undefined
            )
          }
        ]);
      }
      tableStructure.data.push({ cells });
    }
    tableStructure.expander = {
      onClickRow: id => {
        showSecureNote(id);
      },
      idKey: "id",
      currentNoteId: currentNote && currentNote.id
    };
  }

  return (
    <ResponsiveTable
      tableStructure={tableStructure}
      intl={intl}
      changeDimension={showSecureNotePanel}
      autoOverflow={true}
      noDataMessage={
        <FormattedMessage id="noSecureNotes" defaultMessage="No secure notes" />
      }
    />
  );
};

SecureNotesTable.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      owner: PropTypes.string.isRequired,
      user_id: PropTypes.number.isRequired,
      updated_at: PropTypes.string.isRequired,
      access: PropTypes.number.isRequired
    })
  ),
  userId: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  showSecureNote: PropTypes.func.isRequired,
  onClickRemove: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showSecureNotePanel: PropTypes.bool.isRequired
};

export default injectIntl(SecureNotesTable);
